import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import VrLookupField from "./VrLookupField.jsx";
import { VehicleTriplePicker } from "../lib/vehicleCatalogue.jsx";

export default function NewCaseDialog({ enums, onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [selectedVr, setSelectedVr] = useState(null);
  const [form, setForm] = useState({
    alidata_job_card: "OB",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    vehicle_registration: "",
    vin: "",
    brand: enums.brands[0],
    model: "",
    vehicle_generation: "",
    mileage: 0,
    branch: enums.branches[0],
    service_advisor_id: "",
    workshop_supervisor_id: "",
    priority: "Normal",
    customer_complaint: "",
    next_action: "Verificações iniciais na recepção",
    next_action_responsible_id: "",
    next_action_deadline: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16),
    internal_notes: "",
    vr_record_id: null,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const advisors = users.filter((u) => u.role === "service_advisor" && !u.deactivated);
  const supervisors = users.filter((u) => u.role === "workshop_supervisor" && !u.deactivated);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyVr = (vr) => {
    setSelectedVr(vr);
    setForm((f) => ({
      ...f,
      vehicle_registration: vr.registration_plate || f.vehicle_registration,
      vin: vr.vin || f.vin,
      brand: vr.brand || f.brand,
      model: vr.model || f.model,
      vehicle_generation: vr.vehicle_generation || f.vehicle_generation,
      customer_name: vr.customer_name || f.customer_name,
      customer_phone: vr.customer_phone || f.customer_phone,
      customer_email: vr.customer_email || f.customer_email,
      vr_record_id: vr.id,
    }));
  };

  const clearVr = () => {
    setSelectedVr(null);
    setForm((f) => ({ ...f, vr_record_id: null }));
  };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        mileage: parseInt(form.mileage, 10) || 0,
        next_action_deadline: form.next_action_deadline ? new Date(form.next_action_deadline).toISOString() : null,
        next_action_responsible_id: form.next_action_responsible_id || form.service_advisor_id,
        workshop_supervisor_id: form.workshop_supervisor_id || null,
      };
      await api.post("/service-cases", payload);
      onCreated();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-case-dialog">
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Nova Obra de Manutenção</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F4F6F8] rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <VrLookupField selectedVr={selectedVr} onSelect={applyVr} onClear={clearVr} />
          </div>
          <Field label="Alidata job card *" testid="case-alidata">
            <input type="text" value={form.alidata_job_card} onChange={(e) => change("alidata_job_card", e.target.value)} className={inputCls} />
          </Field>
          <div className="col-span-2">
            <label className="text-xs text-[#6C757D] block mb-1">Viatura (Marca · Modelo · Geração) *</label>
            <VehicleTriplePicker
              brand={form.brand}
              model={form.model}
              generation={form.vehicle_generation}
              onChange={({ brand, model, generation }) =>
                setForm((f) => ({ ...f, brand: brand || "", model: model || "", vehicle_generation: generation || "" }))
              }
              required
              testidPrefix="case"
            />
          </div>
          <Field label="Nome do cliente *">
            <input value={form.customer_name} onChange={(e) => change("customer_name", e.target.value)} className={inputCls} data-testid="case-customer-name" />
          </Field>
          <Field label="Telefone do cliente *">
            <input value={form.customer_phone} onChange={(e) => change("customer_phone", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Email do cliente">
            <input type="email" value={form.customer_email} onChange={(e) => change("customer_email", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Dealer">
            <select value={form.branch} onChange={(e) => change("branch", e.target.value)} className={inputCls}>
              {enums.branches.map((b) => <option key={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Matrícula *">
            <input value={form.vehicle_registration} onChange={(e) => change("vehicle_registration", e.target.value.toUpperCase())} className={inputCls + " font-mono"} data-testid="case-plate" />
          </Field>
          <Field label="VIN *">
            <input value={form.vin} onChange={(e) => change("vin", e.target.value.toUpperCase())} className={inputCls + " font-mono"} data-testid="case-vin" />
          </Field>
          <Field label="Quilometragem">
            <input type="number" value={form.mileage} onChange={(e) => change("mileage", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Service Advisor *">
            <select value={form.service_advisor_id} onChange={(e) => change("service_advisor_id", e.target.value)} className={inputCls} data-testid="case-advisor">
              <option value="">Seleccionar…</option>
              {advisors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Supervisor de Oficina">
            <select value={form.workshop_supervisor_id} onChange={(e) => change("workshop_supervisor_id", e.target.value)} className={inputCls}>
              <option value="">Nenhum</option>
              {supervisors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Prioridade">
            <select value={form.priority} onChange={(e) => change("priority", e.target.value)} className={inputCls}>
              {enums.service_case_priorities.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Prazo da próxima acção *">
            <input type="datetime-local" value={form.next_action_deadline} onChange={(e) => change("next_action_deadline", e.target.value)} className={inputCls} />
          </Field>
          <div className="col-span-2">
            <Field label="Queixa do cliente *">
              <textarea value={form.customer_complaint} onChange={(e) => change("customer_complaint", e.target.value)} className={inputCls + " h-20"} data-testid="case-complaint" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Próxima acção *">
              <input value={form.next_action} onChange={(e) => change("next_action", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>
        {error && <div className="mx-5 mb-3 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="new-case-error">{error}</div>}
        <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancelar</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="new-case-submit">
            {submitting ? "A guardar…" : "Criar Obra"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}
